import React from 'react';
import {
  Grid,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { PlatformSettings } from '@/types/company-config';

interface PlatformSettingsTabProps {
  data: PlatformSettings;
  onChange: (field: string, value: string) => void;
  readOnly?: boolean;
}

const PlatformSettingsTab: React.FC<PlatformSettingsTabProps> = ({ data, onChange, readOnly = false }) => {
  const handleChange = (field: string) => (event: { target: { value: string } }) => {
    onChange(field, event.target.value);
  };

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' }
  ];

  const currencies = [
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'CAD', label: 'Canadian Dollar (C$)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'GBP', label: 'British Pound (£)' }
  ];

  const dateFormats = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (International)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' }
  ];

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' }
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Platform Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Default settings for the platform that will be applied to all users unless they override them in their personal preferences.
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth disabled={readOnly}>
            <InputLabel>Default Timezone</InputLabel>
            <Select
              value={data.timezone || 'America/New_York'}
              onChange={handleChange('timezone')}
              label="Default Timezone"
            >
              {timezones.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth disabled={readOnly}>
            <InputLabel>Default Currency</InputLabel>
            <Select
              value={data.currency || 'USD'}
              onChange={handleChange('currency')}
              label="Default Currency"
            >
              {currencies.map((currency) => (
                <MenuItem key={currency.value} value={currency.value}>
                  {currency.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth disabled={readOnly}>
            <InputLabel>Date Format</InputLabel>
            <Select
              value={data.dateFormat || 'MM/DD/YYYY'}
              onChange={handleChange('dateFormat')}
              label="Date Format"
            >
              {dateFormats.map((format) => (
                <MenuItem key={format.value} value={format.value}>
                  {format.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth disabled={readOnly}>
            <InputLabel>Default Language</InputLabel>
            <Select
              value={data.language || 'en'}
              onChange={handleChange('language')}
              label="Default Language"
            >
              {languages.map((lang) => (
                <MenuItem key={lang.value} value={lang.value}>
                  {lang.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box mt={3}>
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong> These settings will be applied as defaults for new users. 
          Existing users can override these settings in their personal preferences.
        </Typography>
      </Box>
    </Box>
  );
};

export default PlatformSettingsTab;